import React, { useState } from "react";
import { Alert, Box, Chip, MenuItem, Stack, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { FileDownloadOutlined as DownloadIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import TableCommon from "../../Common/Table";
import { SearchRecords } from "../../Common/SearchRecords";
import Button from "../../Common/Button";
import { setSnackBar } from "../../../store/common/commonSlice";
import { useGetCommissionsQuery, useLazyGetCommissionsQuery } from "../../../store/shopAdmin/shopAdminApis";
import { handleDateTimeDefault } from "../../../helpers/utils";
import AffiliateDetailsDialog from "./AffiliateDetailsDialog";
import { COMMISSION_STATUS, csvDate, downloadCsv, rupees } from "./affiliateUtils";

const columns = [
    { title: "Date", field: "created_at", sort: false, width: "13%" },
    { title: "Affiliate", field: "affiliate", sort: false, width: "18%" },
    { title: "Order", field: "order", sort: false, width: "16%" },
    { title: "Customer", field: "customer", sort: false, width: "16%" },
    { title: "Order amount", field: "order_amount", sort: false, width: "11%", align: "right" },
    { title: "Reward", field: "amount", sort: false, width: "11%", align: "right" },
    { title: "Status", field: "status", sort: false, width: "15%", align: "center" },
];

const CSV_COLUMNS = [
    ["Date", (r) => csvDate(r.created_at)],
    ["Affiliate", (r) => r.affiliate?.name],
    ["Affiliate mobile", (r) => r.affiliate?.mobile],
    ["Referral code", (r) => r.affiliate?.code],
    ["Order", (r) => r.order_number],
    ["Order status", (r) => r.order_status],
    ["Customer", (r) => r.customer?.name],
    ["Customer mobile", (r) => r.customer?.mobile],
    ["Order amount (₹)", (r) => r.order_amount],
    ["Rule", (r) => r.rule],
    ["Reward (₹)", (r) => r.amount],
    ["Status", (r) => COMMISSION_STATUS[r.status]?.label || r.status],
    ["Credited at", (r) => csvDate(r.credited_at)],
    ["Reversed at", (r) => csvDate(r.reversed_at)],
];

const CommissionLedger = () => {
    const dispatch = useDispatch();
    const [pagination, setPagination] = useState({
        pageNo: 0,
        limit: 10,
        sortBy: "DESC",
        sortField: "created_at",
        keywords: "",
    });
    const [status, setStatus] = useState("all");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [openId, setOpenId] = useState(null);
    const filters = { keywords: pagination.keywords, status, from, to };
    const { data, isFetching, error } = useGetCommissionsQuery({
        page: pagination.pageNo + 1,
        limit: pagination.limit,
        ...filters,
    });
    const [fetchAll, { isFetching: exporting }] = useLazyGetCommissionsQuery();

    const exportCsv = async () => {
        try {
            const all = await fetchAll({ page: 1, limit: 100, ...filters, exportAll: true }).unwrap();
            downloadCsv(
                `magiclock-referral-rewards-${new Date().toISOString().slice(0, 10)}.csv`,
                CSV_COLUMNS,
                all.results,
            );
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "Export failed", severity: "error" }));
        }
    };

    const resetPage = () => setPagination((p) => ({ ...p, pageNo: 0 }));

    const rows =
        !isFetching && data?.results?.length
            ? data.results.map((r) => (
                  <TableRow key={r.id} hover sx={{ "td, th": { border: 0, padding: "14px 12px" } }}>
                      <TableCell>{handleDateTimeDefault(r.created_at)}</TableCell>
                      <TableCell sx={{ cursor: "pointer" }} onClick={() => r.affiliate && setOpenId(r.affiliate.id)}>
                          <Typography variant="body2" fontWeight={600} color="primary">
                              {r.affiliate?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                              {r.affiliate?.code} · {r.affiliate?.mobile}
                          </Typography>
                      </TableCell>
                      <TableCell>
                          <Typography variant="body2">{r.order_number}</Typography>
                          <Typography variant="caption" color="text.secondary">
                              {r.order_status}
                          </Typography>
                      </TableCell>
                      <TableCell>
                          <Typography variant="body2">{r.customer?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                              {r.customer?.mobile}
                          </Typography>
                      </TableCell>
                      <TableCell align="right">{rupees(r.order_amount)}</TableCell>
                      <TableCell align="right">
                          <strong>{rupees(r.amount)}</strong>
                          <Typography variant="caption" display="block" color="text.secondary">
                              {r.rule}
                          </Typography>
                      </TableCell>
                      <TableCell align="center">
                          <Chip size="small" {...(COMMISSION_STATUS[r.status] || { label: r.status })} />
                      </TableCell>
                  </TableRow>
              ))
            : null;

    return (
        <Stack spacing={2}>
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ md: "center" }}
            >
                <Typography variant="subtitle1" fontWeight={700}>
                    Referral rewards
                    <span style={{ color: "gray", fontSize: "10px", verticalAlign: "super", marginLeft: 8 }}>
                        ({data?.count || 0})
                    </span>
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField
                        select
                        size="small"
                        label="Status"
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value);
                            resetPage();
                        }}
                        sx={{ minWidth: 140 }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="credited">Credited</MenuItem>
                        <MenuItem value="reversed">Reversed / cancelled</MenuItem>
                    </TextField>
                    <TextField
                        size="small"
                        type="date"
                        label="From"
                        InputLabelProps={{ shrink: true }}
                        value={from}
                        onChange={(e) => {
                            setFrom(e.target.value);
                            resetPage();
                        }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="To"
                        InputLabelProps={{ shrink: true }}
                        value={to}
                        onChange={(e) => {
                            setTo(e.target.value);
                            resetPage();
                        }}
                    />
                    <Box sx={{ minWidth: 220 }}>
                        <SearchRecords handlePagination={(p) => setPagination(p)} pagination={pagination} />
                    </Box>
                    <Button
                        fullWidth={false}
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportCsv}
                        loading={exporting}
                    >
                        Export CSV
                    </Button>
                </Stack>
            </Stack>
            <Typography variant="caption" color="text.secondary">
                Every referral reward. Search by affiliate name, mobile or code, customer mobile or order number. The
                CSV has the same rows as the filters above.
            </Typography>
            {error ? <Alert severity="error">{error?.data?.message || "Failed to load rewards"}</Alert> : null}
            <TableCommon
                columns={columns}
                tableData={rows}
                count={data?.count || 0}
                loading={isFetching}
                pagination={pagination}
                handlePagination={(p) => setPagination(p)}
                emptyMessage="No referral rewards match these filters"
            />
            <AffiliateDetailsDialog affiliateId={openId} onClose={() => setOpenId(null)} />
        </Stack>
    );
};

export default CommissionLedger;
